import { Injectable } from '@angular/core';
import { AppwriteClientService } from '@prism/angular-shared';
import { ExecutionMethod, ExecutionStatus, Functions, type Models } from 'appwrite';
import { Observable, TimeoutError, catchError, concatMap, filter, from, map, of, switchMap, take, throwError, timer, timeout } from 'rxjs';

const ASYNC_EXECUTION_POLL_INTERVAL_MS = 1_500;
const ASYNC_EXECUTION_TIMEOUT_MS = 135_000;

@Injectable({ providedIn: 'root' })
export class AppwriteFunctionsService {

    private readonly functions: Functions;

    constructor(appwrite: AppwriteClientService) {
        this.functions = new Functions(appwrite.client);
    }

    public post<T = unknown>(functionId: string, path: string, body: unknown): Observable<T> {
        return from(this.functions.createExecution({
            functionId,
            body: JSON.stringify(body),
            xpath: path,
            method: ExecutionMethod.POST,
            async: false,
            headers: {
                'content-type': 'application/json',
            },
        })).pipe(
            map(execution => {
                if (execution.status !== 'completed' || execution.responseStatusCode >= 400) {
                    throw this.executionError(execution.responseBody, execution.errors, execution.responseStatusCode);
                }
                return JSON.parse(execution.responseBody) as T;
            }),
        );
    }

    public postFunction<T = unknown>(functionId: string, body: unknown, path = '/'): Observable<T> {
        return this.post<T>(functionId, path, body);
    }

    /**
     * Starts a POST execution in Appwrite's asynchronous mode and waits for its
     * terminal status. Appwrite does not retain response bodies for async
     * executions, so callers must arrange another durable result channel.
     */
    public postAsyncAndWait(
        functionId: string,
        path: string,
        body: unknown,
        timeoutMs = ASYNC_EXECUTION_TIMEOUT_MS,
    ): Observable<{ executionId: string }> {
        return from(this.functions.createExecution({
            functionId,
            body: JSON.stringify(body),
            xpath: path,
            method: ExecutionMethod.POST,
            async: true,
            headers: {
                'content-type': 'application/json',
            },
        })).pipe(
            switchMap((initialExecution) => this.isExecutionTerminal(initialExecution)
                ? of(initialExecution)
                : timer(ASYNC_EXECUTION_POLL_INTERVAL_MS, ASYNC_EXECUTION_POLL_INTERVAL_MS).pipe(
                    concatMap(() => from(this.functions.getExecution({
                        functionId,
                        executionId: initialExecution.$id,
                    }))),
                    filter((execution) => this.isExecutionTerminal(execution)),
                    take(1),
                    timeout({ first: timeoutMs }),
                )
            ),
            map((execution) => {
                if (execution.status !== ExecutionStatus.Completed || execution.responseStatusCode >= 400) {
                    const statusCode = execution.responseStatusCode >= 400
                        ? execution.responseStatusCode
                        : 500;
                    throw this.executionError(
                        '',
                        execution.errors || `Appwrite function execution ${execution.status}`,
                        statusCode,
                    );
                }
                return { executionId: execution.$id };
            }),
            catchError((error) => error instanceof TimeoutError
                ? throwError(() => Object.assign(
                    new Error(`Appwrite function execution did not finish within ${Math.ceil(timeoutMs / 1000)} seconds.`),
                    { statusCode: 408 },
                ))
                : throwError(() => error)
            ),
        );
    }

    public get<T = unknown>(functionId: string, path: string): Observable<T> {
        return from(this.functions.createExecution({
            functionId,
            xpath: path,
            method: ExecutionMethod.GET,
            async: false,
        })).pipe(
            map(execution => {
                if (execution.status !== 'completed' || execution.responseStatusCode >= 400) {
                    throw this.executionError(execution.responseBody, execution.errors, execution.responseStatusCode);
                }
                return JSON.parse(execution.responseBody) as T;
            }),
        );
    }

    public trigger(functionId: string): Observable<{ executionId: string; status: string }> {
        return from(this.functions.createExecution({ functionId, async: true })).pipe(
            map(execution => ({ executionId: execution.$id, status: execution.status })),
        );
    }

    private isExecutionTerminal(execution: Models.Execution): boolean {
        return execution.status === ExecutionStatus.Completed
            || execution.status === ExecutionStatus.Failed;
    }

    // The thrown error always carries the function's response body under `body` (parsed when it
    // was JSON, raw text otherwise) so callers can dig out an upstream service's own error — e.g.
    // the UPS router relays UPS's `{ data: { response: { errors } } }` verbatim with its 4xx, which
    // has no top-level `message`/`error` and would otherwise collapse into a generic message.
    private executionError(body: string, fallback: string, statusCode: number): Error {
        const generic = fallback || (statusCode >= 400
            ? `Appwrite function returned HTTP ${statusCode}`
            : 'Appwrite function execution failed');
        try {
            const parsed = JSON.parse(body) as { message?: string; error?: string; details?: unknown; method?: string; path?: string };
            const missingRoute = statusCode === 404 && parsed.error === 'not_found'
                && typeof parsed.method === 'string' && typeof parsed.path === 'string';
            const message = parsed.message || (missingRoute
                ? `The API endpoint ${parsed.method} ${parsed.path} is unavailable. Update the API function and try again.`
                : parsed.error) || generic;
            const error = new Error(message);
            Object.assign(error, { statusCode, code: parsed.error, details: parsed.details, body: parsed });
            return error;
        } catch {
            return Object.assign(
                new Error(body || generic),
                { statusCode, body },
            );
        }
    }
}
